import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';

const Testimonials = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      quote: "Julfikar Steel has been our reliable partner for over 5 years. Their consistent quality and on-time delivery have been crucial for our major infrastructure projects.",
      author: "Ahmed Rahman",
      position: "Project Director",
      company: "Metro Construction Ltd.",
      avatar: "AR",
      rating: 5,
      project: "Dhaka Metro Rail Project",
      value: "$2.5M in steel supply"
    },
    {
      id: 2,
      quote: "The quality of their rebar is exceptional. We've used Julfikar Steel products in over 50 high-rise buildings, and the consistency is remarkable.",
      author: "Sarah Johnson",
      position: "Chief Engineer",
      company: "Skyline Developers",
      avatar: "SJ",
      rating: 5,
      project: "Marina Heights Complex",
      value: "15,000 tons delivered"
    },
    {
      id: 3,
      quote: "Their technical support and custom steel solutions helped us meet unique specifications for our industrial facility. Truly professional service.",
      author: "Rajesh Patel",
      position: "Manufacturing Head",
      company: "Industrial Solutions Inc.",
      avatar: "RP",
      rating: 5,
      project: "Automotive Plant Construction",
      value: "Custom grade specifications"
    },
    {
      id: 4,
      quote: "Fast delivery and competitive pricing without compromising on quality. Julfikar Steel understands the needs of the retail construction market.",
      author: "Maria Santos",
      position: "Procurement Manager",
      company: "BuildMart Chain",
      avatar: "MS",
      rating: 5,
      project: "National Retail Expansion",
      value: "500+ locations supplied"
    },
    {
      id: 5,
      quote: "The mill test certificates and quality documentation provided by Julfikar Steel exceed industry standards. Perfect for our compliance requirements.",
      author: "David Chen",
      position: "Quality Assurance Director",
      company: "Pacific Bridge Consortium",
      avatar: "DC",
      rating: 5,
      project: "Cross-Harbor Bridge",
      value: "Zero quality rejections"
    }
  ];


  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const currentTestimonial = testimonials[activeTestimonial];

  return (
    <Section id="testimonials" background="dark" className="relative">
      {/* Section Header */}
      <motion.div
        className="text-center mb-16"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          What Our <span className="text-primary-400">Clients</span> Say
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          Trusted by leading construction companies, developers, and fabricators 
          across the region for consistent quality and reliable service.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
        {/* Main Testimonial */}
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTestimonial}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
            >
              <Card glass={true} className="p-8 lg:p-12 relative">
                {/* Quote Mark */}
                <div className="absolute top-6 left-6 text-6xl text-primary-400/20 font-serif">
                  "
                </div>
                
                <div className="relative z-10">
                  {/* Rating Stars */}
                  <div className="flex items-center mb-6">
                    {[...Array(currentTestimonial.rating)].map((_, i) => (
                      <motion.svg
                        key={i}
                        className="w-5 h-5 text-primary-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </motion.svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className={`text-xl lg:text-2xl ${colors.text.primary} leading-relaxed mb-8 font-light`}>
                    {currentTestimonial.quote}
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary-400 rounded-full flex items-center justify-center text-black font-bold">
                        {currentTestimonial.avatar}
                      </div>
                      <div>
                        <h4 className={`${colors.text.primary} font-semibold`}>{currentTestimonial.author}</h4>
                        <p className={`${colors.text.muted} text-sm`}>{currentTestimonial.position}</p>
                        <p className="text-primary-400 text-sm font-medium">{currentTestimonial.company}</p>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className={`mt-6 pt-6 border-t ${colors.border.muted}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className={colors.text.muted}>Project:</span>
                        <div className={`${colors.text.primary} font-medium`}>{currentTestimonial.project}</div>
                      </div>
                      <div>
                        <span className={colors.text.muted}>Value:</span>
                        <div className="text-primary-400 font-medium">{currentTestimonial.value}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>

          {/* Testimonial Navigation */}
          <div className="flex justify-center mt-8 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeTestimonial 
                    ? 'bg-primary-400 scale-125' 
                    : `${colors.bg.muted} hover:${colors.text.muted}`
                }`}
              />
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">

          {/* Stats */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <Card glass={true} className="p-6">
              <h3 className={`${colors.text.primary} font-semibold mb-6`}>Client Satisfaction</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={`${colors.text.secondary} text-sm`}>Overall Rating</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-primary-400 font-bold">4.9</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {[
                    { label: 'Quality', value: 98 },
                    { label: 'Delivery', value: 96 },
                    { label: 'Service', value: 99 },
                    { label: 'Value', value: 94 }
                  ].map((metric, index) => (
                    <div key={metric.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className={colors.text.muted}>{metric.label}</span>
                        <span className={colors.text.primary}>{metric.value}%</span>
                      </div>
                      <div className={`w-full ${colors.bg.muted} rounded-full h-2`}>
                        <motion.div
                          className="bg-primary-400 h-2 rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${metric.value}%` }}
                          transition={{ delay: index * 0.2, duration: 1 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <Card glass={false} className="p-6 text-center bg-gradient-to-br from-primary-400 to-primary-600">
              <h4 className="text-black font-semibold mb-3">Join Our Satisfied Clients</h4>
              <p className="text-black/80 text-sm mb-4">
                Experience the quality and service that industry leaders trust.
              </p>
              <button 
                className={`w-full ${colors.bg.card} text-primary-400 py-3 rounded-lg font-semibold hover:${colors.bg.muted} transition-colors`}
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Your Quote
              </button>
            </Card>
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

export default Testimonials;